import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Building2, Plus, Search, TrendingUp, TrendingDown, Clock, AlertTriangle,
  CheckCircle2, XCircle, DollarSign, FileText, Edit2, Trash2, ChevronDown,
  ChevronUp, Upload, Download, BarChart3, Settings, CreditCard, Calendar,
  Star, Target, Zap, Shield, Globe, Phone, Mail, MapPin, ExternalLink
} from 'lucide-react';

export default function PayerIntegrations() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [payers, setPayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPayerModal, setShowPayerModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);
  const [editingPayer, setEditingPayer] = useState(null);
  const [selectedPayer, setSelectedPayer] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedPayers, setExpandedPayers] = useState({});
  const [analytics, setAnalytics] = useState({
    totalPayers: 0,
    activePayers: 0,
    totalClaimsSubmitted: 0,
    avgProcessingDays: 0,
    totalReimbursed: 0,
    pendingClaims: 0,
    denialRate: 0,
    topPerformers: []
  });

  // Payer form state
  const [payerForm, setPayerForm] = useState({
    name: '',
    payer_id: '',
    payer_type: 'commercial',
    phone: '',
    fax: '',
    email: '',
    website: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    clearinghouse: 'none',
    clearinghouse_payer_id: '',
    submission_method: 'manual',
    accepts_edi_837p: false,
    accepts_edi_837i: false,
    typical_processing_days: 30,
    notes: ''
  });

  // Rate form state
  const [rateForm, setRateForm] = useState({
    payer_id: '',
    service_code: '',
    service_name: '',
    modifier: '',
    rate_type: 'per_unit',
    rate_amount: '',
    unit_of_measure: '15_minutes',
    effective_date: new Date().toISOString().split('T')[0]
  });

  // Contract state
  const [contracts, setContracts] = useState([]);
  const [showContractModal, setShowContractModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [contractForm, setContractForm] = useState({
    payer_id: '',
    contract_number: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    terms: '',
    auto_renew: false,
    alert_days_before: 30
  });
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  const fetchContracts = useCallback(async () => {
    try {
      const response = await api.get('/payers/contracts');
      setContracts(response.data.contracts || []);
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
      setContracts([]);
    }
  }, []);

  const fetchPayers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/payers?active_only=false');
      setPayers(response.data.payers || []);
    } catch (error) {
      console.error('Failed to fetch payers:', error);
      // Initialize with empty array if endpoint not available
      setPayers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    try {
      // Fetch basic analytics from claims dashboard
      const dashboardRes = await api.get('/claims/dashboard/summary');
      const payersRes = await api.get('/payers?active_only=false');
      const payersList = payersRes.data.payers || [];
      
      setAnalytics({
        totalPayers: payersList.length,
        activePayers: payersList.filter(p => p.is_active).length,
        totalClaimsSubmitted: dashboardRes.data?.total_claims || 0,
        avgProcessingDays: dashboardRes.data?.avg_processing_days || 28,
        totalReimbursed: dashboardRes.data?.total_paid || 0,
        pendingClaims: dashboardRes.data?.pending_claims || 0,
        denialRate: dashboardRes.data?.denial_rate || 0,
        topPerformers: payersList.slice(0, 5)
      });
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      // Set default analytics
      setAnalytics(prev => ({
        ...prev,
        totalPayers: payers.length,
        activePayers: payers.filter(p => p.is_active).length
      }));
    }
  }, [payers]);

  useEffect(() => {
    fetchPayers();
  }, [fetchPayers]);

  useEffect(() => {
    if (payers.length > 0) {
      fetchAnalytics();
    }
  }, [payers, fetchAnalytics]);

  const handleCreatePayer = async () => {
    try {
      if (!payerForm.name) {
        toast.error('Payer name is required');
        return;
      }
      
      await api.post('/payers', payerForm);
      toast.success('Payer created successfully');
      setShowPayerModal(false);
      resetPayerForm();
      fetchPayers();
    } catch (error) {
      console.error('Failed to create payer:', error);
      toast.error(error.response?.data?.detail || 'Failed to create payer');
    }
  };

  const handleUpdatePayer = async () => {
    try {
      await api.put(`/payers/${editingPayer.id}`, payerForm);
      toast.success('Payer updated successfully');
      setShowPayerModal(false);
      setEditingPayer(null);
      resetPayerForm();
      fetchPayers();
    } catch (error) {
      console.error('Failed to update payer:', error);
      toast.error(error.response?.data?.detail || 'Failed to update payer');
    }
  };

  const handleDeletePayer = async (payerId) => {
    if (!window.confirm('Are you sure you want to deactivate this payer?')) return;
    
    try {
      await api.delete(`/payers/${payerId}`);
      toast.success('Payer deactivated');
      fetchPayers();
    } catch (error) {
      console.error('Failed to delete payer:', error);
      toast.error('Failed to deactivate payer');
    }
  };

  const handleCreateRate = async () => {
    try {
      if (!rateForm.service_code || !rateForm.rate_amount) {
        toast.error('Service code and rate amount are required');
        return;
      }
      
      await api.post('/payers/rates', {
        ...rateForm,
        rate_amount: parseFloat(rateForm.rate_amount)
      });
      toast.success('Rate added successfully');
      setShowRateModal(false);
      resetRateForm();
      // Refresh payer rates
      if (selectedPayer) {
        fetchPayerRates(selectedPayer.id);
      }
    } catch (error) {
      console.error('Failed to create rate:', error);
      toast.error(error.response?.data?.detail || 'Failed to add rate');
    }
  };

  const fetchPayerRates = async (payerId) => {
    try {
      const response = await api.get(`/payers/${payerId}/rates`);
      setSelectedPayer(prev => prev ? { ...prev, rates: response.data.rates || [] } : null);
    } catch (error) {
      console.error('Failed to fetch rates:', error);
    }
  };

  const resetPayerForm = () => {
    setPayerForm({
      name: '',
      payer_id: '',
      payer_type: 'commercial',
      phone: '',
      fax: '',
      email: '',
      website: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      zip_code: '',
      clearinghouse: 'none',
      clearinghouse_payer_id: '',
      submission_method: 'manual',
      accepts_edi_837p: false,
      accepts_edi_837i: false,
      typical_processing_days: 30,
      notes: ''
    });
  };

  const resetRateForm = () => {
    setRateForm({
      payer_id: '',
      service_code: '',
      service_name: '',
      modifier: '',
      rate_type: 'per_unit',
      rate_amount: '',
      unit_of_measure: '15_minutes',
      effective_date: new Date().toISOString().split('T')[0]
    });
  };

  const resetContractForm = () => {
    setContractForm({
      payer_id: '',
      contract_number: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      terms: '',
      auto_renew: false,
      alert_days_before: 30
    });
  };

  // Contract handlers
  const handleCreateContract = async () => {
    try {
      if (!contractForm.payer_id || !contractForm.end_date) {
        toast.error('Payer and end date are required');
        return;
      }
      
      await api.post('/payers/contracts', contractForm);
      toast.success('Contract created successfully');
      setShowContractModal(false);
      resetContractForm();
      setEditingContract(null);
      fetchContracts();
    } catch (error) {
      console.error('Failed to create contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to create contract');
    }
  };

  const handleUpdateContract = async () => {
    try {
      await api.put(`/payers/contracts/${editingContract.id}`, contractForm);
      toast.success('Contract updated successfully');
      setShowContractModal(false);
      resetContractForm();
      setEditingContract(null);
      fetchContracts();
    } catch (error) {
      console.error('Failed to update contract:', error);
      toast.error(error.response?.data?.detail || 'Failed to update contract');
    }
  };

  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('Are you sure you want to delete this contract?')) return;
    
    try {
      await api.delete(`/payers/contracts/${contractId}`);
      toast.success('Contract deleted');
      fetchContracts();
    } catch (error) {
      console.error('Failed to delete contract:', error);
      toast.error('Failed to delete contract');
    }
  };

  const openEditContract = (contract) => {
    setEditingContract(contract);
    setContractForm({
      payer_id: contract.payer_id || '',
      contract_number: contract.contract_number || '',
      start_date: contract.start_date || new Date().toISOString().split('T')[0],
      end_date: contract.end_date || '',
      terms: contract.terms || '',
      auto_renew: contract.auto_renew || false,
      alert_days_before: contract.alert_days_before || 30
    });
    setShowContractModal(true);
  };

  // Rate CSV Import/Export
  const handleExportRates = async () => {
    try {
      setExporting(true);
      const response = await api.get('/payers/rates/export', {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'payer_rates.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Rates exported successfully');
    } catch (error) {
      console.error('Failed to export rates:', error);
      toast.error('Failed to export rates');
    } finally {
      setExporting(false);
    }
  };

  const handleImportRates = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/payers/rates/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = response.data;
      if (result.error_count > 0) {
        toast.warning(`Imported ${result.success_count} rates with ${result.error_count} errors`);
      } else {
        toast.success(`Successfully imported ${result.success_count} rates`);
      }
      
      fetchPayers();
    } catch (error) {
      console.error('Failed to import rates:', error);
      toast.error('Failed to import rates');
    } finally {
      setImporting(false);
      event.target.value = ''; // Reset file input
    }
  };

  // Fetch contracts when tab changes
  useEffect(() => {
    if (activeTab === 'contracts') {
      fetchContracts();
    }
  }, [activeTab, fetchContracts]);

  const openEditPayer = (payer) => {
    setEditingPayer(payer);
    setPayerForm({
      name: payer.name || '',
      payer_id: payer.payer_id || '',
      payer_type: payer.payer_type || 'commercial',
      phone: payer.phone || '',
      fax: payer.fax || '',
      email: payer.email || '',
      website: payer.website || '',
      address_line1: payer.address_line1 || '',
      address_line2: payer.address_line2 || '',
      city: payer.city || '',
      state: payer.state || '',
      zip_code: payer.zip_code || '',
      clearinghouse: payer.clearinghouse || 'none',
      clearinghouse_payer_id: payer.clearinghouse_payer_id || '',
      submission_method: payer.submission_method || 'manual',
      accepts_edi_837p: payer.accepts_edi_837p || false,
      accepts_edi_837i: payer.accepts_edi_837i || false,
      typical_processing_days: payer.typical_processing_days || 30,
      notes: payer.notes || ''
    });
    setShowPayerModal(true);
  };

  const togglePayerExpand = async (payerId) => {
    const isExpanding = !expandedPayers[payerId];
    setExpandedPayers(prev => ({ ...prev, [payerId]: isExpanding }));
    
    if (isExpanding) {
      const payer = payers.find(p => p.id === payerId);
      setSelectedPayer(payer);
      await fetchPayerRates(payerId);
    }
  };

  const filteredPayers = payers.filter(payer =>
    payer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payer.payer_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    payer.payer_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPayerTypeColor = (type) => {
    const colors = {
      medicare: 'bg-blue-100 text-blue-700 border-blue-200',
      medicaid: 'bg-green-100 text-green-700 border-green-200',
      commercial: 'bg-purple-100 text-purple-700 border-purple-200',
      workers_comp: 'bg-amber-100 text-amber-700 border-amber-200',
      self_pay: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[type] || colors.commercial;
  };

  const getSubmissionMethodIcon = (method) => {
    switch (method) {
      case 'edi': return <Zap className="w-4 h-4 text-green-500" />;
      case 'portal': return <Globe className="w-4 h-4 text-blue-500" />;
      case 'fax': return <FileText className="w-4 h-4 text-orange-500" />;
      default: return <Settings className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6" data-testid="payer-integrations">
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Payer Integrations
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage insurance payers, rates, and claim configurations
          </p>
        </div>
        <Button
          onClick={() => {
            resetPayerForm();
            setEditingPayer(null);
            setShowPayerModal(true);
          }}
          className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
          data-testid="add-payer-btn"
        >
          <Plus className="w-4 h-4" />
          Add Payer
        </Button>
      </div>

      {/* Executive Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-purple-50/50 p-1 rounded-xl border border-purple-100/60">
          <TabsTrigger 
            value="dashboard" 
            className="data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm rounded-lg gap-2"
          >
            <BarChart3 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger 
            value="payers" 
            className="data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm rounded-lg gap-2"
          >
            <Building2 className="w-4 h-4" />
            Payers ({payers.length})
          </TabsTrigger>
          <TabsTrigger 
            value="rates" 
            className="data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm rounded-lg gap-2"
          >
            <DollarSign className="w-4 h-4" />
            Rate Matrix
          </TabsTrigger>
          <TabsTrigger 
            value="contracts" 
            className="data-[state=active]:bg-white data-[state=active]:text-purple-900 data-[state=active]:shadow-sm rounded-lg gap-2"
          >
            <FileText className="w-4 h-4" />
            Contracts
          </TabsTrigger>
        </TabsList>

        {/* Analytics Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          {/* KPI Cards Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            <div className="card-stat group" data-testid="total-payers-stat">
              <div className="flex items-center gap-4">
                <div className="icon-container bg-gradient-to-br from-purple-100 to-purple-200 border-purple-200">
                  <Building2 className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{analytics.totalPayers}</p>
                  <p className="text-sm text-gray-500">Total Payers</p>
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <span className="text-green-600 font-medium">{analytics.activePayers} active</span>
                <span className="text-gray-400 mx-2">•</span>
                <span className="text-gray-500">{analytics.totalPayers - analytics.activePayers} inactive</span>
              </div>
            </div>

            <div className="card-stat group" data-testid="claims-submitted-stat">
              <div className="flex items-center gap-4">
                <div className="icon-container bg-gradient-to-br from-blue-100 to-blue-200 border-blue-200">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{analytics.totalClaimsSubmitted}</p>
                  <p className="text-sm text-gray-500">Claims Submitted</p>
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <span className="text-amber-600 font-medium">{analytics.pendingClaims} pending</span>
              </div>
            </div>

            <div className="card-stat group" data-testid="total-reimbursed-stat">
              <div className="flex items-center gap-4">
                <div className="icon-container bg-gradient-to-br from-green-100 to-green-200 border-green-200">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{formatCurrency(analytics.totalReimbursed)}</p>
                  <p className="text-sm text-gray-500">Total Reimbursed</p>
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                <span className="text-green-600 font-medium">YTD Revenue</span>
              </div>
            </div>

            <div className="card-stat group" data-testid="avg-processing-stat">
              <div className="flex items-center gap-4">
                <div className="icon-container bg-gradient-to-br from-amber-100 to-amber-200 border-amber-200">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-900">{analytics.avgProcessingDays}</p>
                  <p className="text-sm text-gray-500">Avg Processing Days</p>
                </div>
              </div>
              <div className="mt-3 flex items-center text-sm">
                <span className={`font-medium ${analytics.denialRate > 10 ? 'text-red-600' : 'text-green-600'}`}>
                  {analytics.denialRate.toFixed(1)}% denial rate
                </span>
              </div>
            </div>
          </div>

          {/* Performance Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Payer Performance Scorecard */}
            <Card className="card-premium p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Payer Performance
                </h3>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Star className="w-4 h-4 text-amber-500" />
                  Top performers
                </div>
              </div>
              
              <div className="space-y-4">
                {payers.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No payers configured yet</p>
                    <p className="text-sm mt-1">Add your first payer to see performance metrics</p>
                  </div>
                ) : (
                  payers.slice(0, 5).map((payer, index) => (
                    <div key={payer.id} className="flex items-center gap-4 p-3 bg-gray-50/50 rounded-lg hover:bg-purple-50/50 transition-colors">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-amber-100 text-amber-700' :
                        index === 1 ? 'bg-gray-200 text-gray-700' :
                        index === 2 ? 'bg-amber-600/20 text-amber-800' :
                        'bg-purple-100 text-purple-700'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{payer.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{payer.payer_type?.replace('_', ' ')}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-purple-900">{payer.typical_processing_days || 30} days</p>
                        <p className="text-xs text-gray-500">Avg. processing</p>
                      </div>
                      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                        payer.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {payer.is_active ? 'Active' : 'Inactive'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            {/* Quick Actions / Alerts */}
            <Card className="card-premium p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-lg font-semibold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Quick Actions & Alerts
                </h3>
              </div>
              
              <div className="space-y-3">
                {/* EDI Status Alert */}
                <div className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-100 rounded-lg">
                  <Zap className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-medium text-blue-900">EDI Integration Available</p>
                    <p className="text-sm text-blue-700 mt-0.5">
                      {payers.filter(p => p.accepts_edi_837p).length} payers accept EDI 837P claims
                    </p>
                  </div>
                </div>

                {/* Processing Time Alert */}
                {payers.some(p => p.typical_processing_days > 45) && (
                  <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                    <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-medium text-amber-900">Slow Processing Payers</p>
                      <p className="text-sm text-amber-700 mt-0.5">
                        {payers.filter(p => p.typical_processing_days > 45).length} payers have &gt;45 day processing times
                      </p>
                    </div>
                  </div>
                )}

                {/* Quick Links */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm font-medium text-gray-700 mb-3">Quick Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-start gap-2 text-left"
                      onClick={() => setActiveTab('payers')}
                    >
                      <Plus className="w-4 h-4" />
                      Add New Payer
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-start gap-2 text-left"
                      onClick={() => setActiveTab('rates')}
                    >
                      <DollarSign className="w-4 h-4" />
                      Manage Rates
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-start gap-2 text-left"
                      disabled
                    >
                      <Upload className="w-4 h-4" />
                      Import Rates
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="justify-start gap-2 text-left"
                      disabled
                    >
                      <Download className="w-4 h-4" />
                      Export Data
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Payers List Tab */}
        <TabsContent value="payers" className="space-y-4">
          {/* Search Bar */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search payers by name, ID, or type..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="payer-search-input"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>{filteredPayers.length} payers</span>
            </div>
          </div>

          {/* Payers List */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-3 text-gray-500">Loading payers...</p>
            </div>
          ) : filteredPayers.length === 0 ? (
            <Card className="card-premium p-12 text-center">
              <Building2 className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Payers Found</h3>
              <p className="text-gray-500 mb-6">
                {searchTerm ? 'No payers match your search criteria.' : 'Get started by adding your first insurance payer.'}
              </p>
              {!searchTerm && (
                <Button
                  onClick={() => {
                    resetPayerForm();
                    setShowPayerModal(true);
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First Payer
                </Button>
              )}
            </Card>
          ) : (
            <div className="space-y-3">
              {filteredPayers.map((payer) => (
                <Card 
                  key={payer.id} 
                  className={`card-premium overflow-hidden transition-all duration-300 ${
                    expandedPayers[payer.id] ? 'ring-2 ring-purple-200' : ''
                  }`}
                  data-testid={`payer-card-${payer.id}`}
                >
                  {/* Payer Header */}
                  <div 
                    className="p-5 cursor-pointer hover:bg-purple-50/30 transition-colors"
                    onClick={() => togglePayerExpand(payer.id)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Payer Icon */}
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        payer.payer_type === 'medicare' ? 'bg-blue-100' :
                        payer.payer_type === 'medicaid' ? 'bg-green-100' :
                        'bg-purple-100'
                      }`}>
                        <Building2 className={`w-6 h-6 ${
                          payer.payer_type === 'medicare' ? 'text-blue-600' :
                          payer.payer_type === 'medicaid' ? 'text-green-600' :
                          'text-purple-600'
                        }`} />
                      </div>

                      {/* Payer Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-gray-900 truncate">{payer.name}</h3>
                          <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium border ${getPayerTypeColor(payer.payer_type)}`}>
                            {payer.payer_type?.replace('_', ' ')}
                          </span>
                          {!payer.is_active && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                              Inactive
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 mt-1.5 text-sm text-gray-500">
                          {payer.payer_id && (
                            <span className="flex items-center gap-1">
                              <CreditCard className="w-3.5 h-3.5" />
                              ID: {payer.payer_id}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            {getSubmissionMethodIcon(payer.submission_method)}
                            {payer.submission_method?.toUpperCase() || 'Manual'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {payer.typical_processing_days || 30} days
                          </span>
                        </div>
                      </div>

                      {/* EDI Badges */}
                      <div className="flex items-center gap-2">
                        {payer.accepts_edi_837p && (
                          <span className="px-2 py-1 rounded bg-green-100 text-green-700 text-xs font-medium">
                            837P
                          </span>
                        )}
                        {payer.accepts_edi_837i && (
                          <span className="px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs font-medium">
                            837I
                          </span>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditPayer(payer);
                          }}
                          className="text-gray-500 hover:text-purple-700"
                          data-testid={`edit-payer-${payer.id}`}
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeletePayer(payer.id);
                          }}
                          className="text-gray-500 hover:text-red-600"
                          data-testid={`delete-payer-${payer.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        {expandedPayers[payer.id] ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedPayers[payer.id] && (
                    <div className="border-t border-purple-100 bg-purple-50/20 p-5">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        {/* Contact Information */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            Contact Information
                          </h4>
                          <div className="space-y-2 text-sm">
                            {payer.phone && (
                              <p className="flex items-center gap-2 text-gray-600">
                                <Phone className="w-3.5 h-3.5 text-gray-400" />
                                {payer.phone}
                              </p>
                            )}
                            {payer.fax && (
                              <p className="flex items-center gap-2 text-gray-600">
                                <FileText className="w-3.5 h-3.5 text-gray-400" />
                                Fax: {payer.fax}
                              </p>
                            )}
                            {payer.email && (
                              <p className="flex items-center gap-2 text-gray-600">
                                <Mail className="w-3.5 h-3.5 text-gray-400" />
                                {payer.email}
                              </p>
                            )}
                            {payer.website && (
                              <a 
                                href={payer.website.startsWith('http') ? payer.website : `https://${payer.website}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-purple-600 hover:text-purple-700"
                              >
                                <ExternalLink className="w-3.5 h-3.5" />
                                {payer.website}
                              </a>
                            )}
                            {payer.address_line1 && (
                              <p className="flex items-start gap-2 text-gray-600 mt-2">
                                <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5" />
                                <span>
                                  {payer.address_line1}
                                  {payer.address_line2 && <><br />{payer.address_line2}</>}
                                  {(payer.city || payer.state || payer.zip_code) && (
                                    <><br />{payer.city}{payer.city && payer.state && ', '}{payer.state} {payer.zip_code}</>
                                  )}
                                </span>
                              </p>
                            )}
                            {!payer.phone && !payer.email && !payer.website && !payer.address_line1 && (
                              <p className="text-gray-400 italic">No contact info</p>
                            )}
                          </div>
                        </div>

                        {/* Submission Details */}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                            <Zap className="w-4 h-4" />
                            Submission Details
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500">Method:</span>
                              <span className="font-medium capitalize">{payer.submission_method || 'Manual'}</span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500">Clearinghouse:</span>
                              <span className="font-medium capitalize">{payer.clearinghouse === 'none' ? 'None' : payer.clearinghouse}</span>
                            </div>
                            {payer.clearinghouse_payer_id && (
                              <div className="flex items-center justify-between">
                                <span className="text-gray-500">CH Payer ID:</span>
                                <span className="font-medium">{payer.clearinghouse_payer_id}</span>
                              </div>
                            )}
                            <div className="flex items-center justify-between">
                              <span className="text-gray-500">Processing Time:</span>
                              <span className="font-medium">{payer.typical_processing_days || 30} days</span>
                            </div>
                          </div>
                        </div>

                        {/* Rates Summary */}
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <DollarSign className="w-4 h-4" />
                              Configured Rates
                            </h4>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setRateForm(prev => ({ ...prev, payer_id: payer.id }));
                                setShowRateModal(true);
                              }}
                              className="text-xs gap-1"
                              data-testid={`add-rate-${payer.id}`}
                            >
                              <Plus className="w-3 h-3" />
                              Add Rate
                            </Button>
                          </div>
                          {selectedPayer?.id === payer.id && selectedPayer?.rates?.length > 0 ? (
                            <div className="space-y-2 max-h-32 overflow-y-auto">
                              {selectedPayer.rates.map((rate, idx) => (
                                <div key={idx} className="flex items-center justify-between p-2 bg-white rounded border border-gray-100">
                                  <span className="text-sm font-medium">{rate.service_code}</span>
                                  <span className="text-sm text-purple-700 font-semibold">
                                    ${parseFloat(rate.rate_amount).toFixed(2)}/{rate.unit_of_measure?.replace('_', ' ')}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-gray-400 text-sm italic">No rates configured</p>
                          )}
                        </div>
                      </div>

                      {/* Notes */}
                      {payer.notes && (
                        <div className="mt-4 pt-4 border-t border-purple-100">
                          <p className="text-sm text-gray-600">
                            <span className="font-medium">Notes:</span> {payer.notes}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Rate Matrix Tab */}
        <TabsContent value="rates" className="space-y-4">
          <Card className="card-premium p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Rate Comparison Matrix
                </h3>
                <p className="text-sm text-gray-500 mt-1">Compare service rates across all configured payers</p>
              </div>
              <div className="flex gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleImportRates}
                    className="hidden"
                    data-testid="import-rates-input"
                  />
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="gap-2"
                    disabled={importing}
                    onClick={(e) => e.currentTarget.previousSibling.click()}
                  >
                    <Upload className={`w-4 h-4 ${importing ? 'animate-spin' : ''}`} />
                    {importing ? 'Importing...' : 'Import CSV'}
                  </Button>
                </label>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="gap-2"
                  onClick={handleExportRates}
                  disabled={exporting || payers.length === 0}
                  data-testid="export-rates-btn"
                >
                  <Download className={`w-4 h-4 ${exporting ? 'animate-spin' : ''}`} />
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </Button>
              </div>
            </div>

            {payers.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Target className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No payers configured</p>
                <p className="text-sm mt-1">Add payers first to view rate comparisons</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900 border-b border-purple-100">
                        Service Code
                      </th>
                      {payers.filter(p => p.is_active).slice(0, 6).map(payer => (
                        <th key={payer.id} className="px-4 py-3 text-center text-sm font-semibold text-purple-900 border-b border-purple-100 min-w-[120px]">
                          {payer.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Common service codes */}
                    {['T1019', 'T1020', 'T1021', 'S5125', 'S5130', 'G0162'].map((code, idx) => (
                      <tr key={code} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 border-b border-gray-100">
                          {code}
                        </td>
                        {payers.filter(p => p.is_active).slice(0, 6).map(payer => (
                          <td key={payer.id} className="px-4 py-3 text-center text-sm text-gray-600 border-b border-gray-100">
                            <span className="text-gray-400">-</span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-gray-400 mt-3 text-center">
                  Add rates to payers to see comparison data. Use Import CSV to bulk upload rates.
                </p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Contracts Tab */}
        <TabsContent value="contracts" className="space-y-4">
          <Card className="card-premium p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-semibold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Contract Management
                </h3>
                <p className="text-sm text-gray-500 mt-1">Track payer contracts and renewal dates</p>
              </div>
              <Button
                onClick={() => {
                  resetContractForm();
                  setEditingContract(null);
                  setShowContractModal(true);
                }}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                data-testid="add-contract-btn"
              >
                <Plus className="w-4 h-4" />
                Add Contract
              </Button>
            </div>

            {contracts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p className="font-medium">No Contracts Configured</p>
                <p className="text-sm mt-1">Add contracts to track expirations and renewals</p>
              </div>
            ) : (
              <div className="space-y-3">
                {contracts.map((contract) => {
                  const payer = contract.payers || {};
                  const statusColors = {
                    expired: 'bg-red-100 text-red-700 border-red-200',
                    expiring_soon: 'bg-amber-100 text-amber-700 border-amber-200',
                    active: 'bg-green-100 text-green-700 border-green-200',
                    unknown: 'bg-gray-100 text-gray-700 border-gray-200'
                  };
                  
                  return (
                    <Card key={contract.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`contract-card-${contract.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            contract.status === 'expired' ? 'bg-red-100' :
                            contract.status === 'expiring_soon' ? 'bg-amber-100' : 'bg-green-100'
                          }`}>
                            <FileText className={`w-5 h-5 ${
                              contract.status === 'expired' ? 'text-red-600' :
                              contract.status === 'expiring_soon' ? 'text-amber-600' : 'text-green-600'
                            }`} />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{payer.name || 'Unknown Payer'}</h4>
                            <p className="text-sm text-gray-500">
                              {contract.contract_number ? `#${contract.contract_number} • ` : ''}
                              {new Date(contract.start_date).toLocaleDateString()} - {new Date(contract.end_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <span className={`px-2.5 py-1 rounded-full text-xs font-medium border ${statusColors[contract.status]}`}>
                              {contract.status === 'expired' ? 'Expired' :
                               contract.status === 'expiring_soon' ? `Expires in ${contract.days_until_expiry} days` :
                               'Active'}
                            </span>
                            {contract.auto_renew && (
                              <p className="text-xs text-gray-500 mt-1">Auto-renew enabled</p>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditContract(contract)}
                              className="text-gray-500 hover:text-purple-700"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteContract(contract.id)}
                              className="text-gray-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                      
                      {contract.terms && (
                        <p className="mt-3 text-sm text-gray-600 bg-gray-50 p-2 rounded">
                          {contract.terms}
                        </p>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Payer Modal */}
      {showPayerModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {editingPayer ? 'Edit Payer' : 'Add New Payer'}
                </h3>
                <button
                  onClick={() => {
                    setShowPayerModal(false);
                    setEditingPayer(null);
                    resetPayerForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Basic Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Payer Name *</Label>
                    <Input
                      value={payerForm.name}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Blue Cross Blue Shield"
                      data-testid="payer-name-input"
                    />
                  </div>
                  <div>
                    <Label>Payer ID</Label>
                    <Input
                      value={payerForm.payer_id}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, payer_id: e.target.value }))}
                      placeholder="e.g., 12345"
                    />
                  </div>
                  <div>
                    <Label>Payer Type</Label>
                    <select
                      value={payerForm.payer_type}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, payer_type: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                      data-testid="payer-type-select"
                    >
                      <option value="commercial">Commercial</option>
                      <option value="medicare">Medicare</option>
                      <option value="medicaid">Medicaid</option>
                      <option value="workers_comp">Workers Comp</option>
                      <option value="self_pay">Self Pay</option>
                    </select>
                  </div>
                  <div>
                    <Label>Typical Processing Days</Label>
                    <Input
                      type="number"
                      value={payerForm.typical_processing_days}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, typical_processing_days: parseInt(e.target.value) || 30 }))}
                      min="1"
                      max="120"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={payerForm.phone}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label>Fax</Label>
                    <Input
                      value={payerForm.fax}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, fax: e.target.value }))}
                      placeholder="(555) 123-4568"
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={payerForm.email}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="claims@payer.com"
                    />
                  </div>
                  <div>
                    <Label>Website</Label>
                    <Input
                      value={payerForm.website}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, website: e.target.value }))}
                      placeholder="www.payer.com"
                    />
                  </div>
                </div>
              </div>

              {/* Address */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Address
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label>Address Line 1</Label>
                    <Input
                      value={payerForm.address_line1}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, address_line1: e.target.value }))}
                      placeholder="123 Main Street"
                    />
                  </div>
                  <div>
                    <Label>Address Line 2</Label>
                    <Input
                      value={payerForm.address_line2}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, address_line2: e.target.value }))}
                      placeholder="Suite 100"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>City</Label>
                      <Input
                        value={payerForm.city}
                        onChange={(e) => setPayerForm(prev => ({ ...prev, city: e.target.value }))}
                        placeholder="Anytown"
                      />
                    </div>
                    <div>
                      <Label>State</Label>
                      <Input
                        value={payerForm.state}
                        onChange={(e) => setPayerForm(prev => ({ ...prev, state: e.target.value }))}
                        placeholder="NC"
                        maxLength={2}
                      />
                    </div>
                    <div>
                      <Label>ZIP Code</Label>
                      <Input
                        value={payerForm.zip_code}
                        onChange={(e) => setPayerForm(prev => ({ ...prev, zip_code: e.target.value }))}
                        placeholder="28401"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submission Settings */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Zap className="w-4 h-4" />
                  Submission Settings
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Submission Method</Label>
                    <select
                      value={payerForm.submission_method}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, submission_method: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="manual">Manual</option>
                      <option value="edi">EDI (Electronic)</option>
                      <option value="portal">Payer Portal</option>
                      <option value="fax">Fax</option>
                    </select>
                  </div>
                  <div>
                    <Label>Clearinghouse</Label>
                    <select
                      value={payerForm.clearinghouse}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, clearinghouse: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="none">None</option>
                      <option value="availity">Availity</option>
                      <option value="change_healthcare">Change Healthcare</option>
                      <option value="trizetto">Trizetto</option>
                      <option value="waystar">Waystar</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  {payerForm.clearinghouse !== 'none' && (
                    <div>
                      <Label>Clearinghouse Payer ID</Label>
                      <Input
                        value={payerForm.clearinghouse_payer_id}
                        onChange={(e) => setPayerForm(prev => ({ ...prev, clearinghouse_payer_id: e.target.value }))}
                        placeholder="Clearinghouse-specific ID"
                      />
                    </div>
                  )}
                </div>

                <div className="mt-4 flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={payerForm.accepts_edi_837p}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, accepts_edi_837p: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">Accepts EDI 837P (Professional)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={payerForm.accepts_edi_837i}
                      onChange={(e) => setPayerForm(prev => ({ ...prev, accepts_edi_837i: e.target.checked }))}
                      className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-sm">Accepts EDI 837I (Institutional)</span>
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div>
                <Label>Notes</Label>
                <textarea
                  value={payerForm.notes}
                  onChange={(e) => setPayerForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
                  placeholder="Additional notes about this payer..."
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 rounded-b-2xl flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowPayerModal(false);
                  setEditingPayer(null);
                  resetPayerForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={editingPayer ? handleUpdatePayer : handleCreatePayer}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                data-testid="save-payer-btn"
              >
                <CheckCircle2 className="w-4 h-4" />
                {editingPayer ? 'Update Payer' : 'Add Payer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add Rate Modal */}
      {showRateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  Add Service Rate
                </h3>
                <button
                  onClick={() => {
                    setShowRateModal(false);
                    resetRateForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Service Code *</Label>
                <Input
                  value={rateForm.service_code}
                  onChange={(e) => setRateForm(prev => ({ ...prev, service_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., T1019"
                  data-testid="rate-service-code-input"
                />
              </div>
              <div>
                <Label>Service Name</Label>
                <Input
                  value={rateForm.service_name}
                  onChange={(e) => setRateForm(prev => ({ ...prev, service_name: e.target.value }))}
                  placeholder="e.g., Personal Care Services"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Rate Amount *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={rateForm.rate_amount}
                    onChange={(e) => setRateForm(prev => ({ ...prev, rate_amount: e.target.value }))}
                    placeholder="0.00"
                    data-testid="rate-amount-input"
                  />
                </div>
                <div>
                  <Label>Unit of Measure</Label>
                  <select
                    value={rateForm.unit_of_measure}
                    onChange={(e) => setRateForm(prev => ({ ...prev, unit_of_measure: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="15_minutes">15 Minutes</option>
                    <option value="hourly">Hourly</option>
                    <option value="daily">Daily</option>
                    <option value="visit">Per Visit</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Modifier (Optional)</Label>
                  <Input
                    value={rateForm.modifier}
                    onChange={(e) => setRateForm(prev => ({ ...prev, modifier: e.target.value.toUpperCase() }))}
                    placeholder="e.g., U1, U2"
                    maxLength={4}
                  />
                </div>
                <div>
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={rateForm.effective_date}
                    onChange={(e) => setRateForm(prev => ({ ...prev, effective_date: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRateModal(false);
                  resetRateForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateRate}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                data-testid="save-rate-btn"
              >
                <CheckCircle2 className="w-4 h-4" />
                Add Rate
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Contract Modal */}
      {showContractModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-purple-900" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                  {editingContract ? 'Edit Contract' : 'Add New Contract'}
                </h3>
                <button
                  onClick={() => {
                    setShowContractModal(false);
                    setEditingContract(null);
                    resetContractForm();
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <Label>Payer *</Label>
                <select
                  value={contractForm.payer_id}
                  onChange={(e) => setContractForm(prev => ({ ...prev, payer_id: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="contract-payer-select"
                >
                  <option value="">Select Payer</option>
                  {payers.filter(p => p.is_active).map(payer => (
                    <option key={payer.id} value={payer.id}>{payer.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Contract Number</Label>
                <Input
                  value={contractForm.contract_number}
                  onChange={(e) => setContractForm(prev => ({ ...prev, contract_number: e.target.value }))}
                  placeholder="e.g., CNT-2025-001"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date *</Label>
                  <Input
                    type="date"
                    value={contractForm.start_date}
                    onChange={(e) => setContractForm(prev => ({ ...prev, start_date: e.target.value }))}
                    data-testid="contract-start-date"
                  />
                </div>
                <div>
                  <Label>End Date *</Label>
                  <Input
                    type="date"
                    value={contractForm.end_date}
                    onChange={(e) => setContractForm(prev => ({ ...prev, end_date: e.target.value }))}
                    data-testid="contract-end-date"
                  />
                </div>
              </div>
              
              <div>
                <Label>Alert Days Before Expiry</Label>
                <Input
                  type="number"
                  value={contractForm.alert_days_before}
                  onChange={(e) => setContractForm(prev => ({ ...prev, alert_days_before: parseInt(e.target.value) || 30 }))}
                  min="1"
                  max="180"
                />
              </div>
              
              <div>
                <Label>Terms / Notes</Label>
                <textarea
                  value={contractForm.terms}
                  onChange={(e) => setContractForm(prev => ({ ...prev, terms: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 min-h-[80px]"
                  placeholder="Contract terms or notes..."
                />
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto_renew"
                  checked={contractForm.auto_renew}
                  onChange={(e) => setContractForm(prev => ({ ...prev, auto_renew: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="auto_renew" className="text-sm text-gray-700">Auto-renew contract</label>
              </div>
            </div>

            <div className="border-t border-gray-100 px-6 py-4 flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setShowContractModal(false);
                  setEditingContract(null);
                  resetContractForm();
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={editingContract ? handleUpdateContract : handleCreateContract}
                className="bg-purple-600 hover:bg-purple-700 text-white gap-2"
                data-testid="save-contract-btn"
              >
                <CheckCircle2 className="w-4 h-4" />
                {editingContract ? 'Update Contract' : 'Add Contract'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
